import { forwardRef } from 'react';
import { Card, Group, LoadingOverlay, Stack, Text } from '@/components/ui';
import { CHART_HEIGHT } from '../utils/socialMediaConstants';

interface Props {
  title: string;
  subtitle?: string;
  loading?: boolean;
  /** True when the query succeeded but returned nothing. */
  empty?: boolean;
  emptyMessage?: string;
  /** Shown instead of the chart when the platform no longer provides the metric. */
  unavailableMessage?: string;
  height?: number;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Wrapper for every chart on the section.
 *
 * Centralises three things worth doing once: a consistent card/height so the grid lines
 * up, an honest distinction between "loading", "no data yet" and "this platform no
 * longer provides this metric", and a forwarded ref so a chart can be rasterised for
 * PDF export without each page wiring that up itself.
 */
export const AnalyticsChartCard = forwardRef<HTMLDivElement, Props>(function AnalyticsChartCard(
  { title, subtitle, loading, empty, emptyMessage, unavailableMessage, height = CHART_HEIGHT, actions, children },
  ref,
) {
  return (
    <Card shadow="sm" padding="md" radius="md" withBorder ref={ref} className="report-print-section">
      <Group justify="space-between" align="flex-start" mb="sm" wrap="nowrap">
        <Stack gap={2}>
          <Text fw={600}>{title}</Text>
          {subtitle && <Text size="xs" c="dimmed">{subtitle}</Text>}
        </Stack>
        {actions}
      </Group>

      <div style={{ position: 'relative', minHeight: height }}>
        <LoadingOverlay visible={!!loading} zIndex={1} overlayProps={{ blur: 1 }} />

        {unavailableMessage ? (
          <Stack align="center" justify="center" gap={4} h={height}>
            <Text size="sm" c="dimmed" ta="center" maw={340}>
              {unavailableMessage}
            </Text>
          </Stack>
        ) : empty && !loading ? (
          <Stack align="center" justify="center" h={height}>
            <Text size="sm" c="dimmed">{emptyMessage ?? 'No data for this period'}</Text>
          </Stack>
        ) : (
          children
        )}
      </div>
    </Card>
  );
});
